import { ISysMenuSnapshot } from 'src/models/sys-menu/SysMenu';

export interface MenuItem {
  path: string;
  title: string;
  menuSid: number;
  menuPsid: number | null;
  ordr: number | null;
  children?: MenuItem[];
}

/**
 * API 응답으로 받은 플랫한 메뉴 리스트를 계층 구조로 변환
 * @param menuList - 사용자의 menuList (플랫한 배열)
 * @returns 계층 구조의 메뉴 트리
 */
export function buildMenuTree(menuList: any[]): MenuItem[] {
  if (!menuList || menuList.length === 0) {
    return [];
  }

  // 메뉴를 정렬 (부모 먼저, 그 다음 ordr 순)
  const sortedMenus = [...menuList].sort((a, b) => {
    // menuPsid가 null인 것(최상위 메뉴)을 먼저
    if (a.menuPsid === null && b.menuPsid !== null) return -1;
    if (a.menuPsid !== null && b.menuPsid === null) return 1;

    // 같은 레벨이면 ordr로 정렬
    if (a.menuPsid === b.menuPsid) {
      return (a.ordr || 0) - (b.ordr || 0);
    }

    // 부모가 다르면 menuPsid로 정렬
    return (a.menuPsid || 0) - (b.menuPsid || 0);
  });

  // 메뉴 맵 생성 (빠른 조회를 위해)
  const menuMap = new Map<number, MenuItem>();
  const rootMenus: MenuItem[] = [];

  // 모든 메뉴 아이템을 먼저 맵에 추가
  sortedMenus.forEach((menu) => {
    const menuItem: MenuItem = {
      path: menu.menuUri || '',
      title: menu.menuNm || '',
      menuSid: menu.menuSid,
      menuPsid: menu.menuPsid,
      ordr: menu.ordr,
      // children은 나중에 추가될 경우에만 설정
    };
    menuMap.set(menu.menuSid, menuItem);
  });

  // 부모-자식 관계 설정
  sortedMenus.forEach((menu) => {
    const menuItem = menuMap.get(menu.menuSid);
    if (!menuItem) return;

    if (menu.menuPsid === null || menu.menuPsid === 0) {
      // 최상위 메뉴
      rootMenus.push(menuItem);
    } else {
      // 하위 메뉴 - 부모에 추가
      const parent = menuMap.get(menu.menuPsid);
      if (parent) {
        if (!parent.children) {
          parent.children = [];
        }
        parent.children.push(menuItem);
      }
    }
  });

  return rootMenus;
}

/**
 * 특정 메뉴 URI가 접근 가능한지 확인
 * @param menuList - 사용자의 menuList
 * @param uri - 확인할 URI
 * @returns 접근 가능 여부
 */
export function hasMenuAccess(menuList: any[], uri: string): boolean {
  if (!menuList || menuList.length === 0) return false;
  return menuList.some((menu) => menu.menuUri === uri);
}

/**
 * 메뉴 SID로 접근 가능한지 확인
 * @param menuList - 사용자의 menuList
 * @param menuSid - 확인할 메뉴 SID
 * @returns 접근 가능 여부
 */
export function hasMenuAccessBySid(menuList: any[], menuSid: number): boolean {
  if (!menuList || menuList.length === 0) return false;
  return menuList.some((menu) => menu.menuSid === menuSid);
}
